import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, KeyRound, UserCircle2, History } from "lucide-react";
import { PasswordInput } from "@/components/PasswordInput";

const errMsg = (err) => (typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Erreur");

const Shell = ({ title, children }) => (
  <div className="min-h-[70vh] flex items-center justify-center earth-texture px-4 py-10">
    <div className="w-full max-w-md heritage-card p-8">
      <div className="label-uppercase text-[#993D22] mb-2">Ekom 1 · Evondo</div>
      <h1 className="font-serif-heritage text-3xl text-[#261911] mb-4">{title}</h1>
      {children}
    </div>
  </div>
);

export const ForgotPasswordPage = () => {
  const { API } = useAuth();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await axios.post(`${API}/auth/forgot-password`, { email }); setSent(data.message); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Mot de passe oublié">
      {sent ? <p className="text-sm text-[#2C4230]" data-testid="forgot-sent">{sent}</p> : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <p className="text-sm text-[#6E5B4F]">Saisissez votre adresse e-mail : vous recevrez un lien valable une heure pour choisir un nouveau mot de passe.</p>
          <div><Label>E-mail</Label><Input type="email" required value={email} onChange={e => setEmail(e.target.value)} data-testid="forgot-email-input" /></div>
          <Button className="btn-terra w-full" disabled={busy} data-testid="forgot-submit-btn">{busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Envoyer le lien</Button>
        </form>
      )}
      <Link to="/login" className="block text-sm text-center text-[#993D22] mt-4">Retour à la connexion</Link>
    </Shell>
  );
};

export const ResetPasswordPage = () => {
  const { API } = useAuth();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { toast.error("Les deux mots de passe ne correspondent pas"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token: params.get("token") || "", new_password: pw });
      toast.success("Mot de passe modifié. Vous pouvez vous connecter."); nav("/login");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Nouveau mot de passe">
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <div><Label>Nouveau mot de passe (8 caractères minimum)</Label><PasswordInput required minLength={8} value={pw} onChange={e => setPw(e.target.value)} testid="reset-password-input" /></div>
        <div><Label>Confirmation</Label><PasswordInput required minLength={8} value={pw2} onChange={e => setPw2(e.target.value)} testid="reset-password-confirm-input" /></div>
        <Button className="btn-terra w-full" disabled={busy} data-testid="reset-submit-btn">{busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Enregistrer</Button>
      </form>
    </Shell>
  );
};

const ChangePassword = () => {
  const { API, logout } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.next.length < 8) { toast.error("Le nouveau mot de passe doit comporter au moins 8 caractères"); return; }
    if (f.next !== f.confirm) { toast.error("Les deux mots de passe ne correspondent pas"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: f.current, new_password: f.next });
      toast.success("Mot de passe modifié. Toutes vos sessions ont été fermées : reconnectez-vous.");
      await logout(); nav("/login");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const field = (k, label) => <div><Label>{label}</Label><PasswordInput required value={f[k]} onChange={e => setF(p => ({ ...p, [k]: e.target.value }))} testid={`account-${k}-password`} /></div>;
  return (
    <form onSubmit={submit} className="heritage-card p-6 space-y-4" data-testid="change-password-form">
      <h2 className="font-serif-heritage text-2xl text-[#261911] flex items-center gap-2"><KeyRound className="w-5 h-5 text-[#993D22]" />Modifier mon mot de passe</h2>
      {field("current", "Mot de passe actuel")}{field("next", "Nouveau mot de passe (8 caractères minimum)")}{field("confirm", "Confirmation du nouveau mot de passe")}
      <Button className="btn-terra" disabled={busy} data-testid="change-password-submit">{busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Modifier</Button>
    </form>
  );
};

export const AccountPage = () => {
  const { API, user } = useAuth();
  const [logins, setLogins] = useState([]);
  useEffect(() => { if (user) axios.get(`${API}/auth/my-logins`).then(r => setLogins(r.data)).catch(() => {}); }, [API, user]);
  if (user === false) return <Navigate to="/login" replace />;
  if (!user) return null;
  const roleLabel = user.is_super_admin ? "Super-administrateur" : ({ admin: "Administrateur", elder: "Ancien", contributor: "Contributeur" }[user.role] || "Membre");
  return (
    <div className="max-w-3xl mx-auto px-6 py-10 space-y-6" data-testid="account-page">
      <h1 className="font-serif-heritage text-4xl text-[#261911] flex items-center gap-3"><UserCircle2 className="w-9 h-9 text-[#D9822B]" />Mon compte</h1>
      <div className="heritage-card p-6 text-sm text-[#3A2312] space-y-1">
        <div><span className="text-[#6E5B4F]">Nom :</span> {user.name}</div>
        <div><span className="text-[#6E5B4F]">E-mail :</span> {user.email}</div>
        <div><span className="text-[#6E5B4F]">Rôle :</span> {roleLabel}{!user.validated && !user.is_super_admin && user.role === "contributor" ? " — en attente de validation" : ""}</div>
      </div>
      <ChangePassword />
      <div className="heritage-card p-6" data-testid="my-logins">
        <h2 className="font-serif-heritage text-2xl text-[#261911] mb-3 flex items-center gap-2"><History className="w-5 h-5 text-[#993D22]" />Mes dernières connexions</h2>
        <ul className="text-sm space-y-1">
          {logins.map(l => <li key={l.id} className={l.success ? "text-[#3A2312]" : "text-red-700"}>{l.at?.slice(0, 16).replace("T", " ")} — {l.success ? "réussie" : "échouée"}{l.ip ? ` · ${l.ip}` : ""}</li>)}
          {!logins.length && <li className="italic text-[#6E5B4F]">Aucune connexion enregistrée.</li>}
        </ul>
      </div>
    </div>
  );
};

export default AccountPage;
